import React from 'react';
import { Tab, Box } from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useLocation, useNavigate } from 'react-router-dom';
import withLayout from "../../../hocs/Layout";
import OverallSummary from './overall/OverallSummary';
import ForwardDeliveries from './forward_deliveries/ForwardDeliveries';
import PickupPerformance from './pickup/PickupPerformance';
import RtoSummary from "./rto/RtoSummary"
import NDRSummary from "./ndr/NDRSummary"
import Lost from "./lost/PerformanceLost"
import RVP from './rvp/RVP'
import Stuck from './stuck/Stuck'
import Fake from './fake/Fake'
import DateSelector, { DateRangeContextProvider, DateRangeNote } from '../../../common/contexts/DateSelector';
import { LiveSnapshotNote } from '../../../common/DashboardDateFilter';
import GenericFilters, { FiltersContextProvider } from "../../../common/contexts/Filters";
import PageTitle from '../../../common/PageTitle';
import { BORDER, WHITE } from '../../../styles/foundation/colors';
import { SHADOW } from '../../../styles/foundation/effects';
import { pageContainerSx, tabBarSx } from '../../../styles/patterns';

// Map URL last segment → tab value
const TAB_MAP = {
    analytics: '1', forward: '2', pickup: '3', rto: '4',
    ndr: '5', lost: '6', rvp: '7', stuck: '8', fake: '9',
}
const TAB_TITLES = {
    '1': { title: 'Overall Summary', sub: 'High-level shipment performance across all couriers and zones' },
    '2': { title: 'Forward Deliveries', sub: 'Delivery success rates, TAT adherence, and zone-wise analysis' },
    '3': { title: 'Pickup Performance', sub: 'Pickup timeliness, courier-wise pickup success, and ageing analysis' },
    '4': { title: 'RTO Analysis', sub: 'RTO rates, reasons, courier-wise splits, and trend analysis' },
    '5': { title: 'NDR Analysis', sub: 'Non-delivery reasons, reattempt success, action ageing, and resolution rates' },
    '6': { title: 'Lost & Damaged', sub: 'Lost shipment tracking, courier splits, and zone-wise damage analysis' },
    '7': { title: 'RVP', sub: 'Reverse pickup performance, attempt ageing, and courier-wise success rates' },
    '8': { title: 'Stuck Shipments', sub: 'Shipments stuck in processing — ageing analysis and courier status splits' },
    '9': { title: 'Fake Deliveries', sub: 'Shipments flagged as fake deliveries with conversation history' },
}

function Performance() {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const lastSegment = pathname.split('/').filter(Boolean).pop()
    const value = TAB_MAP[lastSegment] || '1'
    const meta = TAB_TITLES[value]

    const handleChange = (_event, newValue) => {
        const reverseMap = Object.entries(TAB_MAP).find(([, v]) => v === newValue)
        if (reverseMap) {
            const segment = reverseMap[0]
            navigate(segment === 'analytics' ? '/analytics' : `/analytics/${segment}`)
        }
    }

    return (
        <Box sx={pageContainerSx}>
            <PageTitle>Control Tower — {meta.title}</PageTitle>

            {/* <Box sx={pageHeroSx}>
                <Typography sx={heroTitleSx}>{meta.title}</Typography>
                <Typography sx={heroSubSx}>{meta.sub}</Typography>
            </Box> */}

            <DateRangeContextProvider>
                <FiltersContextProvider>
                    <TabContext value={value}>
                        <Box sx={{ bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, mb: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ p: 1.5 }}>
                                {value !== "8" ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                                        <DateRangeNote />
                                        <DateSelector />
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 1.5 }}>
                                        <LiveSnapshotNote
                                            title='Currently stuck shipments'
                                            message='Shows shipments stuck in processing right now, grouped by how long they have been stuck (set the hours threshold below). Because it reflects the current position of every shipment, it is not filtered by a date range.'
                                        />
                                    </Box>
                                )}
                                <br/>
                                <GenericFilters />
                            </Box>
                        </Box>
                       
                        <Box sx={{ bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, mb: 2.5, overflow: 'hidden' }}>
                            <TabList onChange={handleChange} variant='scrollable' value={value}
                                sx={{ ...tabBarSx, mb: 0, px: 1, borderBottom: 'none' }}>
                                <Tab label="Overall Summary" value="1" />
                                <Tab label="Forward Deliveries" value="2" />
                                <Tab label="Pickup Performance" value="3" />
                                <Tab label="RTO" value="4" />
                                <Tab label="NDR Analysis" value="5" />
                                <Tab label="Lost & Damaged" value="6" />
                                <Tab label="RVP" value="7" />
                                <Tab label="Stuck Shipments" value="8" />
                                <Tab label="Fake Deliveries" value="9" />
                            </TabList>
                        </Box>

                        <TabPanel value="1" sx={{ p: 0 }}><OverallSummary /></TabPanel>
                        <TabPanel value="2" sx={{ p: 0 }}><ForwardDeliveries /></TabPanel>
                        <TabPanel value="3" sx={{ p: 0 }}><PickupPerformance /></TabPanel>
                        <TabPanel value="4" sx={{ p: 0 }}><RtoSummary /></TabPanel>
                        <TabPanel value="5" sx={{ p: 0 }}><NDRSummary /></TabPanel>
                        <TabPanel value="6" sx={{ p: 0 }}><Lost /></TabPanel>
                        <TabPanel value="7" sx={{ p: 0 }}><RVP /></TabPanel>
                        <TabPanel value="8" sx={{ p: 0 }}><Stuck /></TabPanel>
                        <TabPanel value="9" sx={{ p: 0 }}><Fake /></TabPanel>
                    </TabContext>
                </FiltersContextProvider>
            </DateRangeContextProvider>
        </Box>
    )
}

export default withLayout(Performance);
